import React, { useCallback, useEffect } from "react"
import { IconX } from "@tabler/icons-react"
import { announcementTagLabel, announcementTagStyle } from "./landingAnnouncements"

type Props = {
  open: boolean
  onClose: () => void
  title: string
  body: string
  categories: string[]
  dateLabel: string
  imageUrl?: string
}

const renderInline = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*|https?:\/\/[^\s]+)/g)

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={index} className="font-semibold text-[#2d1a45]">
          {part.slice(2, -2)}
        </strong>
      )
    }

    if (/^https?:\/\//.test(part)) {
      const href = part.replace(/[),.:]+$/, "")
      const trailing = part.slice(href.length)
      return (
        <React.Fragment key={index}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-dc-primary-900 underline underline-offset-2"
          >
            {href}
          </a>
          {trailing}
        </React.Fragment>
      )
    }

    return <React.Fragment key={index}>{part}</React.Fragment>
  })
}

const LandingAnnouncementModal: React.FC<Props> = ({
  open,
  onClose,
  title,
  body,
  categories,
  dateLabel,
  imageUrl,
}) => {
  const handleClose = useCallback(() => onClose(), [onClose])

  useEffect(() => {
    if (!open) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
    }

    document.addEventListener("keydown", handleEscape)
    document.body.style.overflow = "hidden"

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = ""
    }
  }, [open, handleClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="announcement-modal-title"
      onClick={handleClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex size-9 items-center justify-center rounded-full bg-white/90 text-[#4a454f] shadow-sm transition-colors hover:bg-white"
        >
          <IconX size={20} stroke={1.5} />
        </button>

        {imageUrl && (
          <img src={imageUrl} alt="" className="aspect-[16/9] w-full object-cover" />
        )}

        <div className={`flex flex-col gap-5 px-6 md:px-10 ${imageUrl ? "py-6 md:py-8" : "py-8 md:py-10"}`}>
          <div className="flex flex-wrap items-center gap-3 pr-8">
            {categories.map((category) => (
              <span
                key={category}
                className={`rounded-full px-3 py-1 font-dm-sans text-[10px] font-semibold uppercase tracking-wider ${announcementTagStyle(category)}`}
              >
                {announcementTagLabel(category)}
              </span>
            ))}
            <time className="font-dm-sans text-sm text-[#787878]">{dateLabel}</time>
          </div>

          <h2
            id="announcement-modal-title"
            className="font-outfit text-2xl font-semibold tracking-tight text-[#2d1a45] md:text-3xl"
          >
            {title}
          </h2>

          <div className="font-dm-sans text-base leading-7 text-[#4a454f]">
            {body
              .split(/\n{2,}/)
              .map((paragraph) => paragraph.trim())
              .filter(Boolean)
              .map((paragraph, index) => (
                <p
                  key={index}
                  className={[
                    index > 0 ? "mt-4" : undefined,
                    paragraph.startsWith("#") ? "text-sm leading-6 text-[#787878]" : undefined,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {renderInline(paragraph)}
                </p>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LandingAnnouncementModal
